import { ref } from "vue";
import { subscriptionApi } from "@/services/subscriptionApi";
import { handleApiError } from "@/core/api/handleApiError";

// Shared global state - only one check is made across all components
const isEnabled = ref(false);
const isLoading = ref(true);
const error = ref<string | null>(null);
let hasChecked = false;
let pending: Promise<void> | null = null;

async function checkFeatureStatus(): Promise<void> {
  // Don't check again if already checked
  if (hasChecked && !isLoading.value) {
    return;
  }
  // Coalesce concurrent callers onto the in-flight request so the eager
  // module-load call and a component-driven call don't race into two HTTP
  // round-trips for the same status answer.
  if (pending) {
    return pending;
  }

  pending = (async () => {
    try {
      isLoading.value = true;
      error.value = null;

      const response = await subscriptionApi.getPublicFeatureStatus();
      isEnabled.value = response.subscriptionsEnabled;
      hasChecked = true;
    } catch (err) {
      // Subscriptions are always on (#5653), so a failed read must not hide them.
      isEnabled.value = true;
      // Eager module-load status read — normalize + log with a request id,
      // but stay silent: consumers read `error` and a failed read shouldn't toast.
      error.value = handleApiError(err, {
        fallbackMessage: "Unable to check subscription feature status",
        silent: true,
      }).message;
      hasChecked = true;
    } finally {
      isLoading.value = false;
      pending = null;
    }
  })();
  return pending;
}

// Start checking immediately when module loads
checkFeatureStatus();

/**
 * Composable to check if subscription features should be displayed.
 * Reads the public status endpoint, which always answers enabled since the
 * backend flag was removed (#5653).
 * Uses shared global state - only one API check is made
 */
export function useSubscriptionFeature() {
  return {
    isSubscriptionEnabled: isEnabled,
    isLoading,
    error,
    refresh: checkFeatureStatus,
  };
}
